// IMPORTANT — AI-only nav items are NOT listed in this file.  They live in
// /public/nav-registry.json (single source of truth — read by both React
// and the API's NavRegistrySyncWorker).  Adding a new AI screen = append one
// entry to that JSON.  Both sides pick it up automatically.  (CLAUDE.md §18.8)
import navRegistryJson from "../../public/nav-registry.json";
import { pageGroupId } from "./navData";

interface NavRegistryEntry {
  id: string;
  view: string;
  caption: string;
  parent: string;
  /** 'permission' | 'service-only' | 'admin-or-service' | 'always-visible' */
  mode: string;
}
interface NavRegistryFile {
  version: number;
  items: NavRegistryEntry[];
}
const _registry: readonly NavRegistryEntry[] = (navRegistryJson as unknown as NavRegistryFile).items ?? [];

const _byMode = (m: string) => _registry.filter((e) => e.mode === m).map((e) => e.id);

/**
 * Per-user navigation gating — driven 100% by ALPACA's existing permission
 * tables.  No new tables, no hardcoded user-specific allow-lists.
 *
 * Permission tables consulted server-side (AI_Auth_UserNavPermissions_SP):
 *
 *  1. EmployeeRoleNavigationPermission  user ↔ role link
 *  2. RoleNavigationPermissionDetails   role ↔ NavigationItem (Navigate=1)
 *  3. NavigationItem                    XAF view (NavigationView column)
 *  4. AssignDashboardToUserDepartment   role-CSV ↔ DashboardData
 *  5. DashboardData                     dashboard Oid + title
 *
 * Server returns two sets per user:
 *   • allowedViews       — Set<NavigationView>          (table 1→2→3)
 *   • allowedDashboards  — Set<DashboardId> (lowercase) (table 4→5)
 *
 * Each item id in `navData.ts` is mapped here to one of:
 *   • NAV_VIEW_MAPPING   — list of XAF NavigationViews that grant visibility
 *   • NAV_DASHBOARD_MAPPING — list of DashboardData.Oid GUIDs that grant
 *                             visibility (lowercase, hyphenated)
 *   • ALWAYS_VISIBLE     — items that have no ALPACA row at all (pure
 *                          Alpaca-Intelligence-only pages — Home, Executive
 *                          Dashboard, AVA Insight, etc.)
 *
 * Resolution order
 * ----------------
 *   1. Admin                           → visible
 *   2. Permissions not loaded yet      → visible (boot fall-open)
 *   3. id in ALWAYS_VISIBLE            → visible
 *   4. id in NAV_VIEW_MAPPING and any
 *      mapped view is in allowedViews  → visible
 *   5. id in NAV_DASHBOARD_MAPPING and
 *      any mapped dashboard is in
 *      allowedDashboards               → visible
 *   6. Otherwise                       → HIDDEN
 */

/**
 * Pure-Alpaca pages with no row in any ALPACA table.  Derived from
 * /public/nav-registry.json — every entry with mode='always-visible'.
 * Add new always-visible items by appending to the JSON, NOT here.
 */
export const ALWAYS_VISIBLE: ReadonlySet<string> = new Set(_byMode("always-visible"));

/**
 * Items visible to admin OR the `service` operator account, hidden to other
 * roles regardless of permission grants.  Derived from /public/nav-registry.json —
 * every entry with mode='admin-or-service'.
 *
 * Server-side enforcement: every endpoint owning data for these items
 * independently checks IsAdmin || IsServiceUser before returning data,
 * so a hand-crafted JWT can't reach the rows.  This set just keeps the nav clean.
 */
export const SERVICE_ONLY: ReadonlySet<string> = new Set(_byMode("admin-or-service"));

/**
 * Items reserved for the built-in `service` operator account ONLY — hidden
 * even from other administrators.  Stricter than SERVICE_ONLY.  Derived from
 * /public/nav-registry.json — every entry with mode='service-only'.
 *
 * Each value's rationale lives in the matching JSON $comment block (e.g.
 * "Form Config drives global visibility — locking to the service operator
 * prevents accidental lockout").  Server-side enforcement is independent
 * (controllers check IsServiceUser) so an admin JWT can't reach the endpoint.
 */
export const SERVICE_EXCLUSIVE: ReadonlySet<string> = new Set(_byMode("service-only"));

/**
 * Forms that can NEVER be globally hidden by the "Form Setup" page, no matter
 * what the admin saves.  Without this an admin could switch everything off and
 * lock the whole app — including the very page used to switch things back on.
 *   • `formsetup` — the escape hatch to restore visibility.  Always reachable
 *                   so the admin can never permanently lock themselves out.
 *
 * NOTE: `home` is intentionally NOT protected — per user request the Home form
 * is controlled by the setup like every other form (its nav item hides when
 * switched off).  `formsetup` alone is enough to guarantee recoverability.
 */
export const PROTECTED_FROM_HIDE: ReadonlySet<string> = new Set(["formsetup"]);

/**
 * Alpaca nav id → list of XAF NavigationView ids (any-of semantics).  If
 * any of the listed view ids appears in the user's allowedViews, the item
 * shows.
 */
export const NAV_VIEW_MAPPING: Record<string, string[]> = {
  // ── Sample Scheduling ──────────────────────────────────────────────────
  schedtask: ["TaskSchedulerSetup_ListView", "ScheduledTask_ListView"],
  sampaloc: ["Samplecheckin_ListView_SamplingAllocation", "SamplingAllocation_ListView"],

  // ── Sample Tracking ────────────────────────────────────────────────────
  // GoLive has multiple variants of Sample Registration depending on the
  // user's role flavour ("Copy_Registration" is the most common).
  sampreg: [
    "Samplecheckin_ListView_Copy_Registration",
    "Samplecheckin_ListView_SampleRegistration",
    "Samplecheckin_ListView",
  ],
  sampreceipt: ["Samplecheckin_ListView_SampleReceiptNotification"],
  sampphcheck: ["SampleBottleAllocation_ListView_pHCheck", "SampleBottleAllocation_ListView_pHCheck_Samplecheckin"],

  // ── Barcode Sample Custody (Sample Tracking) ───────────────────────────
  // MAC-gated exactly like ALPACA: each maps to its EXISTING ALPACA
  // NavigationView (verified in AlpacaLims_GoLive.dbo.NavigationItem — the
  // NavigationId equals the caption e.g. "Sample In"), so an admin grants the
  // form to a role from the same MAC screen as every legacy form and only
  // granted users see it. No new NavigationItem row / nav-registry entry needed.
  "sample-in": ["SampleCustodyTest_DetailView_SampleIn"],
  "sample-out": ["SampleCustodyTest_DetailView_SampleOut"],
  "sample-disposal": ["SampleCustodyTest_DetailView_SampleDisposal"],
  "sample-inout-log": ["SampleIn_ListView"],
  "sample-location": ["SampleCustodyTest_DetailView_SampleLocation"],

  // ── Sampling Management ────────────────────────────────────────────────
  // Each Field Data screen anchors ONLY on its own GoLive NavigationView —
  // fixed 2026-07-25 (Issue #1): 'fielddata' (Tracking) previously also
  // accepted the Entry/Review1 grants, so a user with Entry-only access
  // wrongly saw Tracking; and 'fielddata-validation' / 'fielddata-approval'
  // had no explicit mapping at all, so they fell through to the caption
  // fallback — whose caption ("Field Data Validation") doesn't match
  // GoLive's granted caption ("Field Data Review 2"), hiding the item even
  // when the user IS granted FieldDataReview1.
  fielddata: ["SampleLogIn_ListView_FieldDataTracking", "Samplelogin_ListView_FieldDataTracking"],
  "fielddata-entry": ["Samplecheckin_ListView_FieldDataEntry"],
  "fielddata-validation": ["Samplecheckin_ListView_FieldDataReview1"],
  "fielddata-approval": ["Samplecheckin_ListView_FieldDataReview2"],
  samptransfer: ["SampleBottleAllocation_ListView_SampleTransferMain", "Samplecheckin_ListView_SampleTransfer"],

  // ── Subout (Sample SubContract) ────────────────────────────────────────
  suboutreg: ["SampleParameter_ListView_Copy_SubOutPendingSamples"],
  subouttest: ["SubOutSampleRegistrations_ListView_TestOrder", "SampleParameter_ListView_Copy_TestOrder"],
  suboutnotif: [
    "SubOutSampleRegistrations_ListView_NotificationQueue",
    "SubOutSampleRegistrations_ListView_NotificationQueueView",
  ],
  // G5 nav items SuboutSampleResultEntry / Level2SuboutDataReview / SuboutPOTracking.
  suboutresentry: ["SubOutSampleRegistrations_ListView_Copy_ResultEntry"],
  suboutresval: [
    "SubOutSampleRegistrations_ListView_Level2DataReview",
    "SubOutSampleRegistrations_ListView_Level2DataReview_History",
  ],
  suboutpo: ["SubOutSampleRegistrations_ListView_POTracking"],

  // ── Sample Preparation ─────────────────────────────────────────────────
  preplqueue: ["Samplelogin_ListView_SamplePrepQueue", "TestMethod_PrepMethods_ListView_PrepQueue"],
  prepbatches: ["QCBatch_ListView_SamplePrepBatches", "SamplePrepBatch_ListView"],

  // ── Analysis ───────────────────────────────────────────────────────────
  // Daily QC data entry — OG NavigationItem 'DailyQC'; existing role grants carry over.
  dailyqc: ["DailyQCLogBookBuilder_ListView_DailyQC"],
  analysisq: ["TestMethod_ListView_AnalysisQueue"],
  // Result Entry Express — the NAV-LEVEL list view is
  // "Samplecheckin_ListView_ResultEntry": that is the ViewId on the legacy nav
  // item (BTLIMS.Web/Model.xafml) AND the only one of the two below that has a
  // row in dbo.NavigationItem, so it is the only one a role can actually be
  // granted. "SampleParameter_ListView_Copy_ResultEntry" is the INNER parameter
  // view (legacy also has its _Main / _SingleChoice variants) and has NO
  // NavigationItem row, so as the sole mapping it could never match and the
  // form was invisible to EVERY non-admin — even one holding the grant. A WRONG
  // mapping is worse than none here, because the caption-fallback self-heal
  // below is deliberately skipped whenever a mapping exists. Kept as a second
  // entry so any deployment that does resolve it keeps working.
  // (bug report 2026-10-26: Micro III held "Result Entry Express" in
  //  dbo.NavigationItem yet the nav item was hidden in G6 but shown in GoLive.)
  resexpress: ["Samplecheckin_ListView_ResultEntry", "SampleParameter_ListView_Copy_ResultEntry"],
  // SDMS Result Entry — OG NavigationItem id 'Spreadsheet', NavigationView 'SDMS'
  // (dbo.NavigationItem, under DataEntry/Analysis). Grant maps to that view.
  sdms: ["SDMS"],
  // PLM Result Entry — the asbestos-bulk sibling of SDMS. Legacy reaches PLM
  // from the QC Batch screen and has no NavigationItem of its own, so this is
  // an AI-owned view (nav-registry "AI_Plm_ListView"), granted through MAC like
  // any legacy form — the same rule the 2026-09-26 Phase-1 clean-up set for
  // every AI-only screen (no "show to everyone" override).
  plm: ["AI_Plm_ListView"],
  // Settings > Analysis > PLM Reference Library — AI-owned (no ALPACA form).
  "set-plm-reference-library": ["AI_PlmReferenceLibrary_ListView"],
  qcbatches: ["SpreadSheetEntry_AnalyticalBatch_ListView", "QCBatch_ListView"],
  restrack: ["QCBatch_ListView_ResultView"],

  // ── Data Review ────────────────────────────────────────────────────────
  sdmsval: ["SpreadSheetEntry_AnalyticalBatch_ListView_RawDataLevel2BatchReview"],
  // SDMS Result Approval (Level-3 batch grid) — the ALPACA RawDataLevel3BatchReview
  // view. Separate form from the legacy job-level Result Approval (`rappr`). (Merged 2026-07-07.)
  sdmsappr: ["SpreadSheetEntry_AnalyticalBatch_ListView_RawDataLevel3BatchReview"],
  // Legacy job-level Result Validation — the manual counterpart of `rappr` below
  // (ALPACA Model.xafml Data Review group, Caption "Result Validation").
  rval: ["QCBatch_ListView_ResultValidation_Filter"],
  // Legacy job-level Result Approval. Its view drives the sidebar caption override
  // — it MUST be QCBatch_ListView_ResultApproval_Filter (DB caption "Result
  // Approval"), NOT RawDataLevel3BatchReview (DB caption "SDMS Result Approval"),
  // or this item would show the SDMS name and duplicate `sdmsappr`.
  //
  // RawDataLevel3BatchReview used to be listed here as a second view. It is
  // REMOVED (owner report 2026-10-26): it belongs to a DIFFERENT form — SDMS
  // Result Approval, which `sdmsappr` above already owns — and the two carry
  // their own, independent MAC rows:
  //
  //     09_Data Review_Level4 -> Result Approval       R=1 W=0 C=0 D=0
  //     09_Data Review_Level2 -> SDMS Result Approval  R=1 W=1 C=1 D=1
  //
  // navActionsForForm ORs the bits across every view in this array, so listing
  // both made a user holding Level2 pick up WRITE on Result Approval even though
  // Level4 grants Read only — the Rollback / Approve buttons stayed visible on a
  // read-only form. Visibility is unaffected: a user granted the SDMS form still
  // reaches it through `sdmsappr`.
  rappr: ["QCBatch_ListView_ResultApproval_Filter"],

  // ── Reporting ──────────────────────────────────────────────────────────
  custreport: ["CustomReporting_ListView", "Samplecheckin_ListView_Copy_Reporting"],
  repval: ["ReportValidation_ListView", "Reporting_ListView"],
  repdel: ["ReportDelivery_ListView", "Reporting_ListView_Delivery"],
  reptrack: ["ReportTracking_ListView", "Reporting_ListView_Copy_ReportView"],
  eddgen: ["EDDReportGenerator_ListView"],
  morlist: ["DrinkingWaterQualityReports_ListView"],
  // Ad Hoc Reporting is renamed from "MOR Adhoc Reporting" — AI now owns its
  // own NavigationView (AI_AdHocReporting_ListView) so the form has a
  // permission anchor independent of whether the legacy XAF
  // MORAdhocReporting_* rows survive or get retired in ALPACA.
  morreport: ["AI_AdHocReporting_ListView", "MORAdhocReporting_ListView", "MORAdhocReporting_DetailView"],
  // Verified against GoLive NavigationItem 2026-06-11: the legacy view
  // names use the `_ListView_PendingApproval` suffix (NOT `_PendingApproval`).
  // Keep the suffix-less variant too as a fallback in case a customer
  // running an older XAF model still has the short name.
  morpapp: ["DrinkingWaterQualityReports_ListView_PendingApproval", "DrinkingWaterQualityReports_PendingApproval"],
  morpdel: ["DrinkingWaterQualityReports_ListView_PendingDelivery", "DrinkingWaterQualityReports_PendingDelivery"],
  msr: ["MonthlySummaryRepository_ListView"],
  // Settings ▸ Reporting ▸ MSR config forms (merged team package 2026-07-02) —
  // both wrap EXISTING ALPACA XAF screens, so they anchor on the legacy
  // NavigationViews (same admin grant flow as every legacy screen).
  "msr-data-import-config": ["MSRDataImportConfiguration_ListView"],
  "msr-water-system": ["MSRWaterSystem_ListView"],

  // ── User & Security ─────────────────────────────────────────────────────
  // Each maps to the EXISTING ALPACA NavigationView (verified in GoLive +
  // AlpacaLims_AI_Testing) so an admin grants them from the same MAC UI as
  // every legacy screen — no new permission rows needed.
  "sec-users": ["Employee_ListView"],
  "sec-roles": ["RoleNavigationPermission_ListView"],
  "sec-mac": ["ModuleAccessControl_ListView"],
  "sec-rolemac": ["RoleMACConfig_ListView"],

  // ── Inventory Management (ICM) ─────────────────────────────────────────
  // Each id maps to the legacy MAC view of its ALPACA NavigationItem PLUS the AI-only view registered in
  // nav-registry.json. Visibility is unchanged (it used to come from the caption fallback, which reads the
  // same MAC rows); what the mapping adds is the per-form ACTION rights (navActionsForForm) — without an
  // entry they FELL OPEN, so a Read-only role kept every New / Edit / Delete / Submit (bug report 2026-10-21).
  "icm-requisition": ["Requisition_ListViewEntermode", "AI_ICM_Requisition_ListView"],
  "icm-requisition-review": ["Requisition_ListView_Review", "AI_ICM_RequisitionReview_ListView"],
  "icm-purchase-order": ["Requisition_ListView_Purchaseorder_Mainview", "AI_ICM_PurchaseOrder_ListView"],
  "icm-receive-items": ["Requisition_ListView_Receive_MainReceive", "AI_ICM_ReceiveItems_ListView"],
  "icm-distribution": ["Distribution_ListView_MainDistribute", "AI_ICM_Distribution_ListView"],
  "icm-consumption": ["Distribution_ListView_Consumption", "AI_ICM_Consumption_ListView"],
  "icm-fractional": ["Distribution_ListView_Fractional_Consumption", "AI_ICM_FractionalConsumption_ListView"],
  "icm-disposal": ["Distribution_ListView_Disposal", "AI_ICM_Disposal_ListView"],
  "icm-vendor-coa": ["VendorReagentCertificate_ListView", "AI_ICM_VendorCOA_ListView"],
  "icm-item-init": ["AI_ICM_ItemInitialization_ListView"],
  "icm-stock-watch": ["Items_ListView_Copy_StockWatch", "AI_ICM_StockWatch_ListView"],
  "icm-stock-alert": ["Items_ListView_Copy_StockAlert", "AI_ICM_StockAlert_ListView"],
  "icm-expiration-alert": ["Distribution_ListView_Copy_ExpirationAlert", "AI_ICM_ExpirationAlert_ListView"],
  "icm-req-tracking": ["Requisition_ListView_Tracking", "AI_ICM_RequisitionTracking_ListView"],
  "icm-process-tracking": ["Requisition_ListView_History", "AI_ICM_ProcessTracking_ListView"],
  "icm-transaction-history": ["Distribution_ListView_LTsearch", "AI_ICM_TransactionHistory_ListView"],
  "icm-cancelled-requisition": ["Requisition_ListView_Cancelled", "AI_ICM_CancelledRequisition_ListView"],
  "icm-lt-coa-tracking": ["Distribution_ListView_LT_Tracking", "AI_ICM_LtCoaTracking_ListView"],

  // ── Accounting ─────────────────────────────────────────────────────────
  "acct-invoice-queue": ["Samplecheckin_ListView_Invoice"],
  "acct-invoice-validation": ["Invoicing_ListView_Review"],
  "acct-invoice-delivery": ["Invoicing_ListView_Delivery"],
  "acct-invoice-tracking": ["Invoicing_ListView_Tacking"],
  "acct-invoice-edd": ["InvoicingEDDExport_ListView"],
  "acct-quotes-open": ["CRMQuotes_ListView_pendingsubmission"],
  "acct-quotes-tracking": ["CRMQuotes_ListView_Tracking"],
  // CRM ▸ Accounts ▸ Quotes ▸ Open Quotes is a SECOND NavigationItem on the SAME legacy view as Accounting ▸
  // Open Quotes, so the view alone cannot tell them apart (the caption fallback showed the CRM module to an
  // Accounting-only role — bug 18). Gate it on views that exist ONLY under CRM: Quotes Review / Expired /
  // Canceled and the CRM client list. (A role granted ONLY CRM Open Quotes and nothing else in CRM must be
  // given one of these too.)
  "crm-quotes-open": ["CRMQuotes_ListView_PendingReview", "CRMQuotes_ListView_Expired", "CRMQuotes_ListView_Cancel", "Customer_ListView_CRM"],

  // ── Instrument Management ──────────────────────────────────────────────
  "instm-maint-queue": ["MaintenanceTaskCheckList_ListView_MaintenanceQueue"],
  "instm-maint-log": ["MaintenanceSetup_ListView_Log"],

  // ── Reporting ──────────────────────────────────────────────────────────
  "batchreport": ["BatchReporting_DetailView", "AI_BatchReporting_ListView"],

  // ── Settings forms (legacy MAC levels 26_Settings_*) ───────────────────
  // Each Settings form mapped to the legacy NavigationView of its ALPACA NavigationItem (same caption). Visibility is
  // unchanged (it came from the caption fallback, which reads the same MAC rows); the mapping adds the per-form ACTION
  // rights (navActionsForForm) — without an entry they FELL OPEN, so a Read-only role kept every New / Edit / Delete on
  // these ModuleListView forms (audit 2026-10-28). Generated from the live MAC level templates.
  "custreportbuilder": ["CustomReportBuilder"],
  "dashboards": ["DashboardData_ListView"],
  "datefilter": ["DefaultSetting_ListView_DateFilter"],
  "screenautolock": ["ScreenAutoLock_DetailView"],
  "sdms-template-builder": ["DummyClass_ListView_TemplateInfo"],
  "set-bottlesharing": ["BottleSharing_ListView"],
  "set-contractlab": ["SubOutContractLab_ListView"],
  "set-contractlab-test-pricing": ["ContractLabTestPricing_ListView"],
  "set-dailyqc-logbook": ["DailyQCLogBookBuilder_ListView"],
  "set-documentcreator": ["Manual_ListView"],
  "set-eddbuilder": ["EDDBuilder_ListView"],
  "set-email-content-template": ["EmailContentTemplate_ListView"],
  "set-field-tests": ["TestMethod_ListView_FieldTest"],
  "set-flutter-defaultsettings": ["FlutterDefaultSettings_DetailView"],
  "set-flutter-field-template-builder": ["SDATemplate_New_ListView"],
  "set-flutter-fieldapptable": ["tbl_FlutterSDA_FieldEntryColumn_New_ListView_FieldTable"],
  "set-flutter-template-builder": ["SDATemplate_ListView"],
  "set-itemcharge-canceled": ["ItemChargePricing_ListView_Cancaled"],
  "set-parameter-default": ["Testparameter_ListView"],
  "set-parameter-library": ["Parameter_ListView"],
  "set-qualifier-automation": ["QualifierAutomation_ListView"],
  "set-reagent-calceditor": ["RegentPrepCalculationEditor_ListView"],
  "set-report-builder": ["ReportDataV2_ListView"],
  "set-retireddocument": ["Manual_ListView_IsRetired"],
  "set-roundofftest": ["Roundofftest_DetailView"],
  "set-sdms-scientificdata": ["ScientificDataTable"],
  "set-sdms-template-builder": ["DummyClass_ListView_TemplateInfo"],
  "set-sdms-template-types": ["SpreadSheetBuilder_TemplateType_ListView"],
  "set-test-default-pricing": ["TestPriceSurcharge_ListView"],
  "set-test-priority": ["Priority_ListView"],

  // ── Reagent Preparation ────────────────────────────────────────────────
  // Mapped to the legacy MAC views so the form's New / Edit / Delete / Save follow the role's MAC level.
  // WITHOUT an entry navActionsForForm falls OPEN (all true) — a Read-only role kept every action
  // (bug report 2026-10-21, Reagent Prep Log). Visibility is unchanged: these ARE the views the MAC grants.
  "rgp-preplog": ["ReagentPreparation_ListView"],
  "rgp-lltsearch": ["ReagentPreparation_ListView_LLTSearch"],
  // Save on LLT Depletion follows the role's Write right on this view (2026-11-03; the API checks it too).
  "rgp-lltdepletion": ["ReagentPreparation_ListView_LLTDepletion"],

  // ── QA/QC ──────────────────────────────────────────────────────────────
  // The legacy QA/QC MAC levels grant Control Chart (ControlChart_DetailView_New) / Control Chart View
  // (ControlChart_ListView) / MDL Study (MDLStudy_ListView) / Trend Analysis (TrendAnalysis_DetailView) — G6's
  // Control Charts and MDL Studies are registered under AI_* views with different captions, so a legacy grant
  // never matched them and only Trend Analysis showed (bug 19). Accept both the legacy and the AI view.
  "trendanalysis": ["TrendAnalysis_DetailView"],
  "control-charts": ["ControlChart_DetailView_New", "ControlChart_ListView", "AI_ControlChart_ListView"],
  "mdlstudies": ["MDLStudy_ListView", "AI_MDLStudies_ListView"],
  "training-records": ["AI_TrainingRecord_ListView"],

  // ── Data Center ────────────────────────────────────────────────────────
  // Every item maps 1:1 to its GoLive `NavigationItem.NavigationView`.
  "dc-coc": ["COCSettings_ListView_DataCenter"],
  "dc-collector": ["Collector_ListView_ClientCollectors"],
  "dc-compliant": ["CompliantInitiation_ListView_DataCenter"],
  "dc-contact": ["Contact_ListView_Copy_DC"],
  "dc-project": ["Project_ListView_Copy_DC"],
  "dc2-clients": ["Customer_ListView_Copy_DC"],
  "dc-reporting": ["Reporting_ListView_Datacenter"],
  "dc2-documents": ["Manual_ListView_DataCenter"],
  "dc-invoicing": ["Invoicing_ListView_DataCenter"],
  "dc2-items": ["Items_ListView_Copy_DC"],
  "dc2-jobids": ["Samplecheckin_ListView_DataCenter"],
  "dc-samplenotes": ["Samplecheckin_Notes_ListView_DataCenter"],
  "dc-sampleparam": ["SampleParameter_ListView_DataCenter"],
  "dc-samplelogin": ["SampleLogIn_ListView_DataCenter"],
  "dc2-labware": ["Labware_ListView_Copy_DC"],
  "dc2-methods": ["Method_ListView_Copy_DC"],
  // Settings master-data CRUD modules (team delivery 2026-06-13) — gated by
  // their existing ALPACA NavigationViews so an admin grants them from the
  // same MAC UI as every legacy screen.
  // Settings ▸ Method anchors ONLY on the real Settings view — NOT on
  // Method_ListView_Copy_DC (that's the read-only Data Center ▸ Methods
  // viewer, mapped to dc2-methods). Anchoring both here let a user with only
  // the Data Center grant open the Settings editor (user report 2026-07-24).
  "set-methods": ["Method_ListView"],
  "set-holidays": ["Holidays_ListView"],
  // Settings ▸ Dashboard ▸ Assign Dashboard To User (ALPACA port).
  "set-assign-dashboard": ["AssignDashboardToUserDepartment_ListView"],
  // Settings ▸ Test Code + Utility ▸ Test master-data lookups (2026-06-30).
  "set-testcode": ["TestMethod_ListView"],
  "set-container": ["Container_ListView"],
  "set-preservative": ["Preservative_ListView"],
  "set-temperature": ["Temperature_ListView"],
  "set-qctype": ["QCType_ListView"],
  "set-qcrole": ["QcRole_ListView"],
  // Settings master-data lookups (merged team package 2026-07-02) — gated by
  // their existing ALPACA NavigationViews so an admin grants them from the same
  // MAC UI as every legacy screen.
  "set-sample-category": ["SampleCategory_ListView"],
  // Settings ▸ Utility ▸ Test ▸ Sample Field Configuration (VisualMatrix field
  // config, 2026-08-25) — gated by the legacy XAF ListView grant.
  "set-samplefieldconfig": ["VisualMatrix_ListView_SamplingFieldsConfiguration"],
  // Settings ▸ Items / Vendors / Pre-Login Settings — these three had NO entry
  // here, so the global caption-fallback (label ∈ allowedCaptions) was the ONLY
  // thing gating them. ALPACA's Data Center intentionally reuses the SAME
  // display caption for its read-only mirror screens ("Items", "Vendors",
  // "Pre-Login Settings" on Items_ListView_Copy_DC / Vendors_ListView_DataCenter /
  // COCSettings_ListView_DataCenter — see dc2-items/dc2-vendors/
  // dc2-preloginsettings above), so ANY role holding only the Data Center
  // grant was silently unlocking these unrelated Settings-level master-data
  // ADMIN screens purely on a text-label coincidence — confirmed live against
  // a real "Customer" role account (2026-09-26 bug report). Fix: point each id
  // at its OWN distinct, non-DC ALPACA view (Items_ListView / Vendors_ListView /
  // COCSettings_ListView all exist as separate NavigationItem rows from their
  // _Copy_DC / _DataCenter counterparts), so Data Center access no longer
  // implies Settings admin access.
  "set-inventory-items": ["Items_ListView"],
  "set-vendors":          ["Vendors_ListView"],
  "set-prelogin":         ["COCSettings_ListView"],
  // Instrument Management ▸ Instruments (2026-09-26 Phase-1 fix) — the item's
  // label is plural "Instruments", but the real ALPACA caption is SINGULAR
  // "Instrument" on Labware_ListView. A caption-text match would have failed
  // even for a genuinely-permitted user, so this is mapped explicitly instead
  // of relying on the (mismatched) caption fallback.
  "instm-labware": ["Labware_ListView"],
  // CRM ▸ Clients (2026-09-26 Phase-1 fix) — "Clients" is ANOTHER Data-Center
  // caption collision (same class as Items/Vendors/Pre-Login above):
  // Customer_ListView_Copy_DC (Data Center's mirror) shares the caption with
  // the real CRM-scoped Customer_ListView_CRM. Mapped explicitly so
  // Data-Center-only access does not unlock the CRM module.
  "crm-clients": ["Customer_ListView_CRM", "Customer_ListView"],
  // Maintenance ▸ Clients / Retired Clients / Contacts (G5 Maintenance group). "Clients" is a caption collision
  // (Customer_ListView / _CRM / _Copy_DC all read "Clients"), so each is mapped to its own G5 view explicitly.
  "maint-clients": ["Customer_ListView"],
  "maint-retired-clients": ["Customer_ListView_Retired"],
  "maint-contacts": ["Contact_ListView"],
  // G5 Maintenance ▸ Project (dbo.NavigationItem: Project_ListView, Parent "Maintenance").
  "maint-projects": ["Project_ListView"],
  "set-units": ["Unit_ListView"],
  "set-storage": ["Storage_ListView"],
  "set-method-category": ["MethodCategory_ListView"],
  "set-qc-source": ["QCSource_ListView"],
  "set-qc-root-role": ["QCRootRole_ListView"],
  "set-report-category": ["ReportCategory_ListView"],
  "set-report-type": ["ReportType_ListView"],
  "set-crm-category": ["CRMNotesCategory_ListView"],
  "set-request-category": ["ClientRequestCategory_ListView"],
  // Settings ▸ Utility ▸ Client Request ▸ Turnaround. No ALPACA view of its own
  // (dbo.AI_ClientRequestTurnAround is net-new, 2026-09-17), so it rides its
  // sibling's grant and appears/hides with the rest of the Client Request
  // settings folder — the same pattern as pln-limit-type riding
  // PermitLibrary_ListView. Preferred over a hardcoded forced-visible carve-out:
  // this stays permission-gated instead of showing the form to everyone.
  "set-request-turnaround": ["ClientRequestCategory_ListView"],
  // Settings ▸ Client Request ▸ Client Request Priority — net-new, no ALPACA view;
  // rides the same sibling grant as Turnaround so it stays permission-gated.
  "set-request-priority": ["ClientRequestCategory_ListView"],
  // Settings ▸ Client Request ▸ Client Request Service Type — net-new, no ALPACA
  // view; rides the same sibling grant so it stays permission-gated.
  "set-request-service-type": ["ClientRequestCategory_ListView"],
  // Settings ▸ Client Request ▸ Client Request Service Category — net-new, no
  // ALPACA view; rides the same sibling grant so it stays permission-gated.
  'set-request-service-category': ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Client Request Survey Template — net-new,
  // no ALPACA view; rides the same sibling grant so it stays permission-gated.
  'set-request-survey-template':  ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Sample Collection — net-new, no ALPACA
  // view; rides the same sibling grant so it stays permission-gated.
  'set-request-sample-collection': ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Document Type — net-new, no ALPACA view;
  // rides the same sibling grant so it stays permission-gated.
  'set-request-document-type':       ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Fulfilment Method — net-new, no ALPACA view;
  // rides the same sibling grant so it stays permission-gated.
  'set-request-fulfilment-method':       ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Proof Type — net-new, no ALPACA view;
  // rides the same sibling grant so it stays permission-gated.
  'set-request-proof-type':       ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Vehicle — net-new, no ALPACA view; rides
  // the same sibling grant so it stays permission-gated.
  'set-request-vehicle':          ['ClientRequestCategory_ListView'],
  // Settings ▸ Utility ▸ Client Request ▸ Report Template — net-new, no ALPACA view;
  // rides the same sibling grant so it stays permission-gated.
  'set-request-report-template':  ['ClientRequestCategory_ListView'],
  'set-cmdp-sample-category':     ['CMDPSampleCategory_ListView'],
  'set-cmdp-method-param-match':  ['CMDPMethodParamMatch_ListView'],
  'set-asbestos-fibers':          ['AsbestosFibers_ListView'],
  'set-fibercolors':              ['FiberColors_ListView'],
  'set-fiber-types':              ['FiberTypes_ListView'],
  'set-fiber-type-values':        ['FiberTypesValues_ListView'],
  'set-materials':                ['Materials_ListView'],
  'set-non-asbestos-fibers':      ['Non_AsbestosFibers_ListView'],
  'set-non-fibrous-components':   ['Non_FibrousComponents_ListView'],
  'set-other-components':         ['OtherComponents_ListView'],
  'set-result-range':             ['ResultRange_ListView'],
  'set-unconventional-analysis':  ['DataEntry_ListView'],
  // Utility ▸ Analysis ▸ Asbestos ▸ PLM Sample Notes + Daily Qc (2026-09-10),
  // gated by their legacy XAF ListViews.
  "set-plm-sample-notes": ["PLMSampleComments_ListView"],
  // PLM Dispersion Oil (2026-10-08) — the lab's own list view; its NavigationItem
  // already exists (Settings / Utility / Analysis / Asbestos), so roles are granted
  // the same way as for its siblings.
  "set-plm-dispersion-oil": ["DispersionOil_ListView"],
  // PLM Instrument Setup (2026-10-15) — the G5 list view of the same table.
  "set-plm-instrument-setup": ["PlmInstrumentSetup_ListView"],
  // Data Center › PLM Contamination Check (2026-10-15) — the G5 view of the same query.
  "dc-plm-contamination": ["PLMV2_Contamination_DetailView"],
  // Analysis › PLM Daily QC (2026-10-16) — the G5 list view; its NavigationItem
  // (PLMV2DailyQc) already exists, so roles are granted as in G5.
  // PLM Daily QC — this mapping is CORRECT and matches the legacy nav item
  // (Model.xafml: Id="PLMV2DailyQc" ViewId="PlmV2DailyQc_ListView"). It is
  // nonetheless invisible to every non-admin because dbo.NavigationItem has NO
  // row for PlmV2DailyQc_ListView, so no role can be granted it. GoLive still
  // shows the item because XAF builds its nav from Model.xafml, which this view
  // is not permission-gated through; G6 gates everything through NavigationItem.
  // The fix is to PROVISION the grant (add the NavigationItem row + role grant),
  // NOT to force it visible here — that forced-true carve-out was removed on
  // 2026-09-26 precisely because it showed the form to roles with no grant.
  "plm-dailyqc": ["PlmV2DailyQc_ListView"],
  "set-dailyqc-datatable": ["DailyQcDataTable_ListView"],
  "set-dailyqc-category": ["DailyQCCategory_ListView"],
  "acc-paymentterms": ["PaymentTerms_ListView"],
  "acc-paymentterms-setup": ["Customer_ListView_PaymentTermsSetup", "PaymentTerms_ListView"],
  "acc-region": ["Region_ListView"],
  // Utility ▸ Audit Trail / Client / Client Account Number (2026-09-10),
  // gated by their legacy XAF ListViews.
  "set-reasonforchange": ["AuditTrailReasontoChange_ListView"],
  "set-client": ["Client_ListView"],
  "set-clientaccountnumber": ["ClientAccountNumber_ListView"],
  // Utility ▸ e-Data Transfer (2026-09-11), gated by their legacy XAF ListViews.
  "set-parsersetup": ["InstrumentSoftware_ListView"],
  "set-parametermatch": ["ParameterMatch_ListView"],
  "set-qctypematch": ["QCTypeMatch_ListView"],
  "set-icpmsmasspick": ["ICP_MS_MassPick_ListView"],
  // Utility ▸ Document Management ▸ Document ▸ Category (2026-09-11).
  "set-documentcategory": ["DocumentCategory_ListView"],
  // Utility ▸ EDD Builder ▸ Category (2026-09-11).
  "set-eddcategory": ["EDDCategory_ListView"],
  // Utility ▸ Glossary ▸ Category (2026-09-11).
  "set-glossarycategory": ["GlossaryCategory_ListView"],
  // Utility ▸ Internal Audit ▸ NCA ▸ Action Category / NCA Reason / Problem Category (2026-09-11).
  "set-actioncategory": ["ActionCategory_ListView"],
  "set-ncareason": ["NCAReason_ListView"],
  "set-problemcategory": ["ProblemCategory_ListView"],
  // Utility ▸ Inventory Management (ICM) simple masters (2026-09-11).
  "set-delivery-priority": ["DeliveryPriority_ListView"],
  "set-grade": ["Grades_ListView"],
  "set-item-categories": ["Category_ListView"],
  "set-manufacturers": ["Manufacturer_ListView"],
  "set-package-units": ["Packageunits_ListView"],
  "set-icm-project": ["ICMProject_ListView"],
  "set-shipping-options": ["Shippingoptions_ListView"],
  "set-ship-via": ["ShipVia_ListView"],
  "set-icm-storage": ["ICMStorage_ListView"],
  "set-vendor-evaluation-items": ["VendorEvaluationItem_ListView"],
  // Utility ▸ Maintenance ▸ Security ▸ Version Log ▸ Deployment Team (2026-09-11).
  "set-deployment-team": ["DeploymentTeam_ListView"],
  // Utility ▸ Inspection ▸ Inspection Category (2026-09-11).
  "set-inspection-category": ["InspectCategory_ListView"],
  // Utility ▸ Instrument ▸ Instrument Category (2026-09-11).
  "set-instrument-category": ["LabwareCategory_ListView"],
  // Utility ▸ Instrument ▸ Maintenance Setup (2026-09-11).
  "set-maintenance-category": ["MaintenanceCategory_ListView"],
  "set-freq-code": ["FreqCode_ListView"],
  "set-service-company": ["ServiceCompany_ListView"],
  "set-skip-reason": ["SkipReason_ListView"],
  "set-task-checklist": ["TaskCheckList_ListView"],
  "set-task-ownership": ["TaskOwnership_ListView"],
  // Utility ▸ Routes (2026-09-11).
  "set-routes": ["Routes_ListView"],
  // Document Management ▸ Qualifier — legacy dbo.Qualifiers (Phase 1, 2026-09-22).
  "qualifier": ["Qualifiers_ListView"],
  // Utility ▸ Sample Sites (2026-09-11).
  "set-sample-sites": ["SampleSites_ListView"],
  // Settings ▸ Instrument Management (legacy "LabwareSettings", 2026-09-18).
  "set-labware": ["Labware_ListView"],
  "set-labware-certificate": ["LabwareCertificate_ListView"],
  "set-maintenance-setup": ["MaintenanceSetup_ListView"],
  // Utility ▸ Workflow Status Chain (legacy StatusDefinition_ListView, 2026-09-12).
  "set-workflow-status-chain": ["StatusDefinition_ListView"],
  // Utility ▸ Sample Tracking ▸ Collectors (legacy Collector_ListView, 2026-09-12).
  "set-collectors": ["Collector_ListView"],
  // Utility ▸ Sample Tracking ▸ Sample Registration — 7 nodes (2026-09-12).
  "set-check-points-setup": ["SampleConditionCheckData_ListView"],
  "set-sample-matrix-field-setup": ["VisualMatrix_ListView_FieldSetup"],
  "set-enotification-template": ["eNotificationContentTemplate_ListView"],
  "set-payment-status": ["PaymentStatus_ListView"],
  "set-job-inactivation": ["JobInactivation_ListView"],
  // Utility ▸ Sample Sites lookup group — 5 lookups (legacy Utility Index 27, 2026-09-12).
  "set-pws-id": ["PWSSystemID_ListView"],
  "set-pws-name": ["PWSSystem_ListView"],
  "set-sample-type": ["SampleType_ListView"],
  "set-system-type": ["SystemTypes_ListView"],
  "set-water-type": ["WaterTypes_ListView"],
  // Utility ▸ Sample Login lookup group — 14 lookups (legacy Utility Index 28, 2026-09-16).
  "set-sl-bare": ["Bare_ListView"],
  "set-sl-code": ["SLCode_ListView"],
  "set-sl-component": ["SLComponent_ListView"],
  "set-sl-kind": ["SLKind_ListView"],
  "set-rejection-criteria": ["RejectionCriteria_ListView"],
  "set-repeat-location": ["RepeatLocation_ListView"],
  "set-sample-status": ["SampleStatus_ListView"],
  "set-sl-sanitarian": ["SLSanitarian_ListView"],
  "set-sanitarian-temperature": ["SanitarianTemperature_ListView"],
  "set-site-source": ["SiteSource_ListView"],
  "set-sl-size": ["Sizes_ListView"],
  "set-user-defined-dropdown": ["UserDefinedDropdown1_ListView"],
  "set-sl-air-volume": ["SLAirVolume_ListView"],
  "set-sl-total-surface-area": ["SLTotalSurfaceArea_ListView"],
  // Utility ▸ Topic (legacy top-level leaf, 2026-09-16).
  "set-topic": ["Topic_ListView"],
  // Utility ▸ Subout Tracking (legacy group Index 29, 2026-09-17).
  'set-sublabparammatch':            ['SubLabParameterMatch_ListView'],
  'set-sublabqcparammatch':          ['SubLapQCParamMatch_ListView'],
  // Utility ▸ Test Pricing ▸ Item Charge Pricing ▸ Category (legacy group Index 30, 2026-09-17).
  'set-itemchargepricing-category':  ['ItemChargePricingCategory_ListView'],
  // Utility ▸ Test — simple/mild lookup masters (legacy group Index 31, 2026-09-17).
  'set-accreditation':               ['Accrediation_ListView'],
  'set-broughtby':                   ['BroughtBy_ListView'],
  'set-holdingtime':                 ['HoldingTimes_ListView'],
  'set-matrix':                      ['Matrix_ListView'],
  'set-preptypes':                   ['PrepTypes_ListView'],
  'set-preservecondition':           ['PreserveCondition_ListView'],
  'set-sample-matrix':               ['VisualMatrix_ListView'],
  'set-tabcontrols':                 ['TabControls_ListView'],
  // Utility ▸ Test — complex bespoke views wired read-only (list only, 2026-09-17).
  'set-container-settings':          ['ContainerSettings_ListView'],
  'set-instruments-field':           ['TestMethod_ListView_FieldInstrument'],
  'set-instruments-lab':             ['TestMethod_ListView_TestInstrument'],
  'set-instruments-sampleprep':      ['TestMethod_ListView_SamplePrep_TestInstrument'],
  'set-tabfieldconfig':              ['TabFieldConfiguration_ListView'],
  // Utility ▸ UnFollow Settings (legacy singleton leaf Index 35, 2026-09-17).
  'set-unfollow-settings':           ['UnFollowSettings_ListView'],
  // Utility ▸ QA/QC (2026-09-11).
  "set-study-id": ["StudyID_ListView"],
  "set-study-name": ["StudyName_ListView"],
  "set-source": ["Source_ListView"],
  "set-qc-category": ["QCCategory_ListView"],
  // Utility ▸ Reagent Prep (2026-09-11).
  "set-calculation-approach": ["CalculationApproach_ListView"],
  "set-reagent-operator": ["ReagentOperator_ListView"],
  "set-standard-name": ["StandardName_ListView"],
  "set-reagent-units": ["ReagentUnits_ListView"],
  "set-ws-prep-type": ["WSPrepType_ListView"],
  "set-ws-storage-name": ["WSStorageName_ListView"],
  // Utility ▸ Reporting (2026-09-11).
  "set-coverpage-nelap": ["CoverPageNELAP_ListView"],
  "set-msr-category": ["MSRCategory_ListView"],
  // Water System + MSR Data Import Config reuse the existing 'msr-water-system' /
  // 'msr-data-import-config' permission entries (already mapped above).
  // Utility ▸ Prep Options (2026-09-11).
  "set-prep-options": ["PrepOptions_ListView"],
  // Turn Around Time — legacy top-level Settings leaf; rides its own ALPACA view (2026-09-17).
  "set-turn-around-time": ["TurnAroundTime_ListView"],
  // Technical Writer — legacy HelpCenter FAQ list; rides its own ALPACA view (2026-09-17).
  "set-technical-writer": ["HelpCenter_ListView_FAQ_Articles"],
  // Utility ▸ Project Category (2026-09-11).
  "set-project-category": ["ProjectCategory_ListView"],
  // External Data Import — Permit Libraries settings (Phase 1) + the 3-tab
  // import form (Reporting).  Gated by the legacy ALPACA NavigationViews (same
  // admin grant flow as every legacy screen).  Mail Setup + Service Period are
  // popups on the External Data Import toolbar, not standalone nav items.
  "edi-permit-types": ["PermitTypes_ListView", "PermitLibrary_ListView"],
  // Settings ▸ Permit Library New ▸ Permit Type — same underlying ALPACA
  // NavigationView as edi-permit-types (both read dbo.PermitTypes), so an admin
  // grant covers old and new without provisioning a second view.
  // Settings ▸ Permit Library New ▸ Permit Library New — same view as
  // edi-permit-library (both read dbo.PermitLibrary).
  "pln-permit-library": ["PermitLibrary_ListView"],
  "pln-permit-type": ["PermitTypes_ListView", "PermitLibrary_ListView"],
  // Settings ▸ Permit Library New ▸ Limit Type. No ALPACA view of its own (the
  // screen is net-new in the v5 prototype), so it rides the Permit Library grant
  // and appears/hides with the rest of the group.
  "pln-limit-type": ["PermitLibrary_ListView"],
  // Both new screens are read-only views over permit data, so they ride the
  // same permission as the rest of the group rather than minting a new one.
  // 2026-09-18: Permit Type / Limit Type / Operators now sit under Settings ▸
  // Utility ▸ Permit and Permit Limit under Data Center — nav placement only;
  // the grants below are keyed by id and did not change.
  'pln-operators':           ['PermitLibrary_ListView'],
  // Settings ▸ Permit Library ▸ Permit Row Colors (2026-10-21) — the result-grid colour
  // rules ride the Permit Library grant too; PermitRowColorsController checks the SAME view.
  'pln-permit-row-colors':   ['PermitLibrary_ListView'],
  'pln-permit-limits':       ['PermitLibrary_ListView'],
  'edi-permit-library':      ['PermitLibrary_ListView'],
  'edi-permit-setup':        ['PermitLibrary_ListView', 'Permitsetup_ListView'],
  'edi-import':              ['ExternalDataImport'],
  'edi-sheet-mapping':       ['ExternalDataImport', 'SuboutImportSourceDetails_DetailView'],
  'edi-limits':              ['ExternalDataImport', 'MCLAndSCLLimits_ListView_EDI', 'MCLAndSCLLimits_ListView'],
  // Settings ▸ System Management + Analysis Department Chain (merged 2026-06-26).
  "adc-chain": ["TestMethod_ListView_AnalysisDepartmentChain", "AnalysisDepartmentChain_ListView"],
  "sm-city": ["City_ListView"],
  "sm-division": ["Company_ListView"],
  "sm-division-department": ["Company_ListView"],
  "sm-glossary": ["Glossary_ListView"],
  "sm-message-editor": ["MessageEditor_ListView"],
  "sm-country": ["CustomCountry_ListView"],
  "sm-language": ["CurrentLanguage_ListView"],
  "sm-department": ["Department_ListView"],
  "sm-position": ["Position_ListView"],
  "sm-state": ["CustomState_ListView"],
  "sm-timezones": ["TimeZones_ListView"],
  "dc-noncon": ["NonConformityInitiation_ListView_DataCenter"],
  // QA/QC ▸ Internal Audit — the G5 NavigationItem views (ComplaintInitiation, CorrectiveActionVerificationCompliant, NonconformityInitiation, CorrectiveActionVerificationNonconformity). History rides the Verification grant.
  "qa-cc-init": ["CompliantInitiation_ListView"],
  "qa-cc-verif": ["CompliantInitiation_ListView_Verification"],
  "qa-cc-hist": ["CompliantInitiation_ListView_Verification"],
  "qa-nca-init": ["NonConformityInitiation_ListView"],
  "qa-nca-verif": ["NonConformityInitiation_ListView_PendingVerification"],
  "qa-nca-hist": ["NonConformityInitiation_ListView_PendingVerification"],
  "dc2-paramlibrary": ["Parameter_ListView_Copy_DC"],
  "dc2-preloginparams": ["COCSettingsTest_ListView_Copy_DataCenter"],
  "dc2-preloginsettings": ["COCSettings_ListView_DataCenter"],
  "dc2-analyticalbatches": ["SpreadSheetEntry_AnalyticalBatch_ListView_Copy_DC"],
  "dc-qctype": ["QCType_ListView_DC"],
  "dc-crmquotes": ["CRMQuotes_ListView_DataCenter"],
  "dc-resultqc": ["SampleParameter_ListView_Copy_DC_ResultQC"],
  "dc2-resultsamples": ["NpBtlimsDC_ListView"],
  // Rejected Login Samples (client ticket 70544) — same underlying data + permission
  // as the Sample Login Data Center view, so it inherits that gate.
  "dc2-rejectedloginsamples": ["SampleLogIn_ListView_DataCenter"],
  "dc-sdmsbatch": ["SpreadSheetEntry_AnalyticalBatch_ListView_Copy_SDMSBatchResults"],
  "dc2-sdmsseq": ["SpreadSheetBuilder_SequencePattern_ListView_DataCenter"],
  // Settings ▸ SDMS ▸ Sequence Setup — the CRUD form (OG Settings ListView).
  "set-sdms-seqsetup": ["SpreadSheetBuilder_SequencePattern_ListView"],
  "dc2-sdmstemplates": ["DummyClass_ListView_TemplateInfo_DataCenter"],
  "dc2-testcomponent": ["Component_ListView_Datacenter"],
  "dc2-testcontainers": ["TestMethod_TestGuides_ListView_DataCenter"],
  "dc2-testpricing": ["TestPriceSurcharge_ListView_DataCenter"],
  "dc2-testholdingtime": ["HoldingTimes_ListView_DataCenter"],
  "dc2-testinstruments": ["TestMethod_ListView_Instrument_DC"],
  "dc-testparam": ["Testparameter_ListView_Test_SampleParameter_DataCenter"],
  "dc-prepmethods": ["TestMethod_PrepMethods_ListView_DataCenter"],
  "dc2-testqcparamdefaults": ["Testparameter_ListView_Test_QCSampleParameter_QCParameterDefault_DataCenter"],
  "dc2-tests": ["TestMethod_ListView_Copy_DC"],
  "dc-loginlog": ["LoginLog_ListView_DataCenter"],
  "dc2-userpermission": ["UserRightDC_ListView"],
  "dc2-userroleright": ["RoleNavigationPermission_RoleNavigationPermissionDetails_ListView_DataCenter"],
  "dc2-users": ["Employee_ListView_DataCenter"],
  "dc2-vendors": ["Vendors_ListView_DataCenter"],

  // ── Audit Trail Log ────────────────────────────────────────────────────
  "at-custreport": ["AuditData_ListView_CustomReporting"],
  "at-eddgen": ["AuditData_ListView_EDDReportGenerator"],
  "at-fielddata": ["AuditData_ListView_FieldDataEntry"],
  "at-l3subout": ["AuditData_ListView_Level3SuboutDataReview"],
  "at-mac": ["AuditData_ListView_ModuleAccessControl"],
  "at-morreport": ["AuditData_ListView_DrinkingWaterQualityReports"],
  "at-morpapp": ["AuditData_ListView_DWQRPendingApproval"],
  "at-morpdel": ["AuditData_ListView_DWQRPendingDelivery"],
  "at-openquotes": ["AuditData_ListView_OpenQuotes"],
  "at-prelogin": ["AuditData_ListView_PreLoginSettings"],
  "at-projectnotes": ["AuditData_ListView_ProjectNotes"],
  "at-reptrack": ["AuditData_ListView_ReportTracking"],
  "at-repval": ["AuditData_ListView_ReportValidation"],
  "at-rappr": ["AuditData_ListView_ResultApproval"],
  "at-restrack": ["AuditData_ListView_ResultEntry"],
  "at-sampreg": ["AuditData_ListView_Sampleregistration"],
  "at-samplesites": ["AuditData_ListView_SampleSites"],
  "at-samptransfer": ["AuditData_ListView_SampleTransfer"],
  "at-sampaloc": ["AuditData_ListView_SamplingAllocation"],
  "at-suboutresentry": ["AuditData_ListView_SuboutSampleResultEntry"],
  "at-suboutresval": ["AuditData_ListView_SuboutSampleResultValidation"],
  "at-subouttest": ["AuditData_ListView_SuboutTestOrder"],
  "at-tests": ["AuditData_ListView_Test"],

  // ── Intelligence-only forms (registered in dbo.NavigationItem with AI_*
  // NavigationViews so admins grant them from the SAME MAC screen as legacy
  // forms — they have NO DefaultSetting rows, so ALPACA's own menu query can
  // never surface them; grants only affect this app). Flipped from
  // always-visible to permission mode 2026-07-24.
  // Task Tracker forms exist in BOTH apps — accept the AI_ grant OR the
  // user's existing ALPACA grant for the equivalent legacy form, so users
  // who already have Task Calendar etc. in ALPACA keep them here (parity).
  taskcal: ["AI_TaskCalendar_DetailView", "ReminderActivity_ListView"],
  jobprog: ["AI_JobProgress_ListView", "Samplecheckin_ListView_Incompletejobs"],
  testprog: ["AI_TestProgress_ListView", "Samplecheckin_ListView_ProjectTracking"],
  // mdlstudies: mapped with the QA/QC block above (legacy MDLStudy_ListView + AI view).
  // Client Request — Request Registration. BOTH views: a user is granted EITHER
  // the legacy ALPACA view (ClientRequest_ListView, captioned "Request
  // Registration") OR the new AI view, so map both — visibility (line 843) and
  // the action gate then match whichever the user actually holds. Mapping only
  // the AI view hid the form for users on the legacy grant (owner report
  // 2026-10-23).
  reqreg: ["AI_ClientRequest_ListView", "ClientRequest_ListView"],
  servermon: ["AI_ServerMonitor_DetailView"],
  "servermon-history": ["AI_ServerHistory_ListView"],
  histres: ["AI_HistoricResults_ListView"],
  // My Details follows the SAME rule as ALPACA: visible only when the user's
  // role grants the legacy Employee_DetailView ("My Details") — not always-on.
  mydetails: ["Employee_DetailView"],
  // Executive Dashboard — MAC-grantable module anchor (Analytics group),
  // registered in dbo.NavigationItem like the other AI-only forms.
  execdash: ["AI_ExecutiveDashboard_DetailView"],
};

/**
 * Alpaca nav id → list of DashboardData.Oid GUIDs (lowercase, hyphenated).
 * If any of those Oids is in the user's allowedDashboards set, the item
 * shows.  Sample Backlog / ICM dashboards are gated via
 * AssignDashboardToUserDepartment.Role and NOT via NavigationItem.
 */
export const NAV_DASHBOARD_MAPPING: Record<string, string[]> = {
  "bl-coc": ["be9f6612-9e6c-445d-909f-edfaa7e107ef"], // 00_Missing_COC
  "bl-due": ["2cc552d4-2561-4547-bae8-3281fe85b275"], // Backlog 00 - Due Samples
  "bl-track": ["6e01ba52-e002-48c9-a30c-1704f5c812e5"], // Backlog 01 - Sample Tracking
  "bl-transfer": ["caf0d00d-174e-4623-9897-d1c6b8d6d5ad"], // Backlog 02 - Pending Transfer (DashboardData; gated by AssignDashboardToUserDepartment.Role)
  "bl-subout": ["bef0d249-b1ad-4bc8-91f3-4957128fba60"], // Backlog 02 - Subout Tracking
  "bl-prep": ["26223032-b5bc-43d9-b4bf-36ddb502fb65"], // Backlog 03 - Sample Preparation
  "bl-sdms": ["6511d25e-8713-4844-b24d-53f58a4c30cb"], // Backlog 04 - SDMS Result Entry
  "bl-review": ["adab47e1-c5bf-4755-a34c-77b709f8437d"], // Backlog 06 - Data Review
  "bl-report": ["7a225ac9-f748-49ff-baa6-51966d66ad24"], // Backlog 08 - Reporting
  "bl-rappr": ["4fd68683-329d-4c9f-ae3b-66eaf736a00c"], // Backlog 07 - Result Approval (DashboardData created 2026-08-24; gated by AssignDashboardToUserDepartment.Role)
  icm01: ["7a62d440-fd67-4934-b4ae-0145181c140a"], // ICM01 - Pending Orders
  icm02: ["fb8f1b64-1b80-47e6-a2f4-345352ecb4f0"], // ICM02 - Pending Validation
  icm03: ["ab1139ef-0333-423e-9d84-7ad9f30184c7"], // ICM03 - Pending Receipt
  icm05: ["80894fda-a469-438c-a5e2-c443e062bed7"], // ICM05 - Pending COA
  // Pending Jobs — a TABLE dashboard since 2026-09-28 (deploy/db/AI_Dashboard_PendingJobs_Seed.sql,
  // fixed Oid); was the hand-built PendingJobsPage.  Gated by its assignment like the rest — this
  // mapping wins over its nav-registry "always-visible" mode (checked first in canSeeNavItem).
  "bl-pjobs": ["f508beb5-b240-4233-9a66-7cfbd7472341"], // Pending Jobs
};

/**
 * DYNAMIC NAV (2026-10-06): register a backlog dashboard nav id → its
 * DashboardData.Oid at RUNTIME, so a dashboard discovered from the assignment
 * table (AI_Nav_BacklogDashboards_SP → /api/v1/backlog/nav-dashboards) both
 *   • ROUTES to the dashboard viewer (backlogDashboardId reads this map), and
 *   • is gated by the EXISTING allowedDashboards visibility check
 *     (canSeeNavItem step 5) — one visibility authority, no new gate.
 * Idempotent: a known static id is simply re-set to the same Oid. Mutating a
 * property of the const map is fine (const forbids reassignment, not mutation).
 */
export function registerDashboardNav(id: string, oid: string): void {
  NAV_DASHBOARD_MAPPING[id] = [oid.toLowerCase()];
}

/** Normalize a caption for whitespace-and-case-insensitive comparison. */
export function normCaption(s: string): string {
  return s.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * What the user was granted INSIDE the Settings and the Data Center sections (owner request 2026-10-28: "show only the
 * forms I granted — I did not grant Settings, so do not show Settings or Data Center").
 *
 * A NavigationView is shared between sections: Settings ▸ Instrument and Instrument Management ▸ Instruments are both
 * `Labware_ListView`, and a Data Center audit viewer carries the same caption as the form it audits. Matching on the view /
 * caption alone therefore opened a whole Settings or Data Center folder for a user who only held the OTHER section. Each
 * granted row also carries the path of the NavigationItem it was granted on (`itempath`, e.g.
 * `Application/NavigationItems/Items/Settings/Items/…`), so a Settings / Data Center form is shown only when that path is
 * itself under that section.
 */
export interface SectionGrants {
  settings:   { views: string[]; captions: string[] };
  datacenter: { views: string[]; captions: string[] };
}

const SECTION_PATH = {
  settings:   "application/navigationitems/items/settings/",
  datacenter: "application/navigationitems/items/datacenter/",
} as const;

/** One granted row of the permissions payload (`allowedNavViews`). */
export interface GrantedNavRow { navigationView?: string | null; navigationCaption?: string | null; itempath?: string | null }

/** Fold the granted rows into the per-section view / caption lists. Views are lowercased and captions normalised, as the
 *  matcher below compares them. */
export function buildSectionGrants(rows?: ReadonlyArray<GrantedNavRow> | null): SectionGrants {
  const out: SectionGrants = {
    settings: { views: [], captions: [] },
    datacenter: { views: [], captions: [] },
  };
  for (const r of rows ?? []) {
    const path = (r.itempath ?? "").toLowerCase();
    const key = path.startsWith(SECTION_PATH.settings) ? "settings"
      : path.startsWith(SECTION_PATH.datacenter) ? "datacenter" : null;
    if (!key) continue;
    if (r.navigationView) out[key].views.push(r.navigationView.toLowerCase());
    if (r.navigationCaption) out[key].captions.push(normCaption(r.navigationCaption));
  }
  return out;
}

/**
 * Returns true when the given nav item should be shown to the current user.
 *
 * Resolution order:
 *   1. Admin                                          → visible
 *   2. Permissions not loaded yet                     → visible (boot fall-open)
 *   3. id in ALWAYS_VISIBLE                           → visible
 *   4. NAV_VIEW_MAPPING[id] ∩ allowedViews            → visible
 *   5. NAV_DASHBOARD_MAPPING[id] ∩ allowedDashboards  → visible
 *   6. **Global fallback** — item.label ∈ allowedCaptions → visible
 *   7. Otherwise                                      → HIDDEN
 *
 * The caption fallback (step 6) is what makes the gating self-healing:
 * even if I haven't mapped a NavigationView variant for a particular role,
 * the user still sees the item as long as one of their permitted
 * NavigationCaption strings matches the item's label.  GoLive's
 * `NavigationCaption` is the display name shown in their sidebar — so by
 * definition a match means "this user is allowed to see something with the
 * same name in GoLive."
 *
 * NavigationView strings + captions are compared case-insensitively
 * (allowedViews + the mapping table are lowercased at write-time).
 */
export function canSeeNavItem(
  id: string,
  label: string,
  allowedViews: ReadonlySet<string>,
  allowedDashboards: ReadonlySet<string>,
  allowedCaptions: ReadonlySet<string>,
  isAdmin: boolean,
  username: string = "",
  hiddenForms: ReadonlySet<string> = new Set(),
  // TRUE once the login/permissions payload has been applied (the auth store's
  // `permissions` object is non-null). Distinguishes "still booting, nothing
  // loaded yet" (fail OPEN to avoid a flickering-empty sidebar) from "loaded,
  // and this non-admin role legitimately has NOTHING linked" (fail CLOSED —
  // ALPACA NavigationViewHideController parity: an unlinked role sees an empty
  // menu, not every form). Defaults false so any caller that doesn't opt in
  // keeps the old fail-open-when-empty behaviour. (BUG-2 fix 2026-09-03.)
  permissionsLoaded: boolean = false,
  // Grants made INSIDE Settings / Data Center (see SectionGrants). Absent on a session saved before this existed — the
  // matcher then behaves as it did, until the permissions refresh on the next load supplies it.
  sectionGrants?: SectionGrants | null,
): boolean {
  // A Documents CATEGORY leaf (`documents:<oid>`) is not its own permission
  // target — it inherits the Documents grant. ALPACA does make each category
  // grantable, but those rows carry 0 grants across all 19 roles on the customer
  // DB, and ALPACA keys them on the category NAME so a rename silently drops the
  // grant. Resolving to the parent keeps one grant to maintain and no rename
  // trap. (Owner request 2026-10-12 put the categories back in the sidebar; this
  // is what makes them visible without 2 new permission rows per category.)
  if (id.startsWith("documents:")) { id = "documents"; label = "Documents"; }

  const isServiceUser = username.trim().toLowerCase() === "service";

  // ── GLOBAL "Form Setup" gate ────────────────────────────────────────────
  // A form switched OFF on the admin Form Setup page is hidden for EVERYONE
  // (admins included) — this is the master visibility switch the user asked
  // for.  A tiny protected set can never be hidden (lockout guard).  Checked
  // FIRST so a global hide always wins over per-user permission.
  if (hiddenForms.has(id) && !PROTECTED_FROM_HIDE.has(id)) return false;

  // Service-EXCLUSIVE items: ONLY the `service` operator account — hidden even
  // from admins.  Checked before the isAdmin short-circuit below.
  if (SERVICE_EXCLUSIVE.has(id)) return isServiceUser;

  // Service-only items: visible to admin OR the `service` operator account,
  // hidden to everyone else regardless of permissions data.
  if (SERVICE_ONLY.has(id)) return isAdmin || isServiceUser;

  // Dashboard-driven nav items (Sample Backlog / ICM) are gated by their
  // AssignDashboardToUserDepartment assignment for EVERYONE — admins/Service
  // included. ALPACA parity: a dashboard whose assignment is inactive (or has no
  // matching role) is HIDDEN even for the Service/admin user; an inactive/no-role
  // Sample Backlog dashboard does not appear in the nav. Checked BEFORE the
  // isAdmin short-circuit below (the permission SP now returns admins the ACTIVE
  // dashboard set, so this compares against real data, not the old empty-set
  // that the admin bypass rendered as "show every dashboard"). (2026-08-28)
  const dashOids = NAV_DASHBOARD_MAPPING[id];
  if (dashOids) {
    // Still booting — no permission data has arrived yet — show to avoid the
    // nav flickering empty on first paint. Once loaded, an unmatched dashboard
    // assignment correctly hides the item (fail closed).
    if (!permissionsLoaded) return true;
    return dashOids.some((o) => allowedDashboards.has(o.toLowerCase()));
  }

  if (isAdmin) return true;
  // Still booting (permissions not applied yet) — fall open to avoid the sidebar
  // flashing empty on first paint. NOT the same as "loaded and empty": a loaded
  // non-admin role with nothing linked falls through to the mapping checks below
  // and gets hidden (fail closed — ALPACA parity). (BUG-2 fix 2026-09-03.)
  if (!permissionsLoaded) return true;
  if (ALWAYS_VISIBLE.has(id)) return true;

  // Performance Analytics dashboards (`pa-*`) — one MODULE-level permission
  // anchor (dbo.NavigationItem 'AI_PerformanceAnalytics_ListView', grantable
  // from the MAC screen like any legacy form) controls the whole module.
  if (id.startsWith("pa-")) return allowedViews.has("ai_performanceanalytics_listview");

  // Notification pages (`nc-*`) — same pattern: one module-level anchor
  // ('AI_Notifications_ListView') grants all notification settings pages.
  if (id.startsWith("nc-")) return allowedViews.has("ai_notifications_listview");

  // ── Phase-1 modules — NOT a permission bypass (2026-09-26) ──────────────
  // "Phase-1" means ONLY: this module has no dedicated NAV_VIEW_MAPPING entry
  // of its own yet. It never again means "show to everyone" — every id below
  // (resexpress, reformula, formulalib, sdmsformula, reqreg, svcaloc, svcsched,
  // svctrack, rttrack, set-samplefieldconfig, the 10 icm-* ids, the acct-*/
  // instm-*/crm-*/rgp-* shells, subouttest, suboutnotif) now falls through to
  // the SAME resolution every other id uses, a few lines below: NAV_VIEW_MAPPING
  // → NAV_DASHBOARD_MAPPING → caption-fallback → hidden. No `return true` here
  // for any of them — a "Customer"-type role with no matching grant now sees
  // NONE of these, exactly like GoLive (bug report 2026-09-26).
  //
  // Verified against the live ALPACA NavigationItem table before removing the
  // blanket overrides:
  //   • resexpress / subouttest / suboutnotif / set-samplefieldconfig already
  //     had a real NAV_VIEW_MAPPING entry — removing the override was enough.
  //   • reqreg, svctrack, rttrack, all 11 acct-*, all 3 rgp-*, 14 of 15 crm-*,
  //     and all 10 icm-* ids have a genuine, distinctly-captioned ALPACA
  //     NavigationItem (e.g. "Item Requisition", "Invoice Queue", "Agenda") —
  //     the existing caption-fallback below now gates them correctly with NO
  //     code change needed here. (icm-purchase-order/icm-receive-items/
  //     icm-vendor-coa DO have real captions — "Ordering Items"/"Receiving
  //     Items"/"Vendors COA" — correcting an earlier comment that said they
  //     didn't.)
  //   • "instm-labware" and "crm-clients" needed an explicit NAV_VIEW_MAPPING
  //     entry instead of the caption fallback — see those entries above for
  //     why (a caption mismatch and a Data-Center collision, respectively).
  //   • "formulalib", "reformula", "sdmsformula", "svcaloc", "svcsched" have
  //     NO caption or view under ANY role in ALPACA today — there is nothing
  //     for the fallback to match. They are genuinely new AI-only screens, so
  //     they now fall through to `return false` and are effectively
  //     admin-only (via the isAdmin check above) until each gets its own
  //     AI_*_ListView entry in nav-registry.json that an admin can grant
  //     through MAC. Flagged here rather than silently hidden: if a non-admin
  //     needs one of these five before that grant work lands, that is a real
  //     product gap to close (add the nav-registry entry + grant it), not a
  //     bug in this file.

  // Per-form AI audit logs (`at-aiform-*`) — dynamic leaves under the "Audit
  // Trail Log AI" module (built by AppShell from forms that have audit rows).
  // Visible exactly when that module's consolidated leaf (at-ai) is, so the
  // per-form inventory can never diverge from the module's own permission.
  if (id.startsWith("at-aiform-"))
    return canSeeNavItem(
      "at-ai",
      label,
      allowedViews,
      allowedDashboards,
      allowedCaptions,
      isAdmin,
      username,
      hiddenForms,
      permissionsLoaded,
    );

  const views = NAV_VIEW_MAPPING[id];

  // Settings / Data Center forms are shown only for a grant made INSIDE that section — a view or caption granted on a
  // NavigationItem in another module (Instrument Management, Reporting …) never opens them.
  const section = pageGroupId[id] === "grp-settings" ? "settings" : pageGroupId[id] === "grp-datacenter" ? "datacenter" : null;
  if (section && sectionGrants) {
    const granted = sectionGrants[section];
    if (views) return views.some((v) => granted.views.includes(v.toLowerCase()));
    return granted.captions.includes(normCaption(label));
  }

  if (views && views.some((v) => allowedViews.has(v.toLowerCase()))) return true;

  const dashboards = NAV_DASHBOARD_MAPPING[id];
  if (dashboards && dashboards.some((d) => allowedDashboards.has(d.toLowerCase()))) return true;

  // ── Caption-fallback — self-heals future mapping mismatches as long as our
  // label matches GoLive's caption.  ONLY for items with NO explicit mapping:
  // a mapped form must be granted through its mapped views/dashboards.  Without
  // this restriction a user holding only the AUDIT-TRAIL view of a form (the
  // AuditData_* captions are word-for-word the form names — "Custom Reporting",
  // "Report Validation", "EDD Report Generator") would unlock the REAL form.
  if (!views && !dashboards && allowedCaptions.has(normCaption(label))) return true;

  return false;
}

// ─── Per-form ACTION permissions (Read / Write / Create / Delete) ──────────
//
// Distinct from the NAVIGATE gating above (which decides whether a form is
// VISIBLE at all).  Once a form is visible, these decide which mutating buttons
// it may show: New (Create), Edit (Write), Delete (Delete).  Source of truth is
// the user's assigned Module Access Control (Role-Based MAC) level templates —
// the server folds them into a NavigationView → {R,W,C,D} map (login payload
// `navActions`, computed by AI_Auth_UserNavPermissions_SP result set 4).

/** Read / Write / Create / Delete a user effectively holds for one form. */
export interface NavActions {
  read: boolean;
  write: boolean;
  create: boolean;
  delete: boolean;
}

/** Everything allowed — the fall-open default (admins, or where no MAC data exists). */
export const ALL_NAV_ACTIONS: NavActions = { read: true, write: true, create: true, delete: true };

/** One raw row from the server `navActions` payload (fields optional so both the
 *  strict login-response type and the looser silent-relogin type are assignable). */
export interface RawNavActionRow {
  navigationView?: string | null;
  canRead?: boolean;
  canWrite?: boolean;
  canCreate?: boolean;
  canDelete?: boolean;
}

/**
 * Fold the server payload rows into a lowercased `NavigationView → NavActions`
 * map.  Lowercased so lookups match `NAV_VIEW_MAPPING` (compared case-insensitively).
 */
export function buildNavActionMap(rows?: ReadonlyArray<RawNavActionRow> | null): Record<string, NavActions> {
  const m: Record<string, NavActions> = {};
  for (const r of rows ?? []) {
    const v = (r.navigationView ?? "").toLowerCase();
    if (!v) continue;
    m[v] = { read: !!r.canRead, write: !!r.canWrite, create: !!r.canCreate, delete: !!r.canDelete };
  }
  return m;
}

/**
 * Effective actions for a nav id, MAX-aggregated across the id's mapped
 * NavigationViews (a user can do X if ANY mapped view grants X).
 *
 * Fall-OPEN (all true) when: the user is an admin, the action map is empty
 * (permissions not loaded yet, or the tenant runs no MAC model), OR the form
 * has no action entry at all.  So the gate only ever TIGHTENS a form where MAC
 * data actually exists — it never strips access a form has today.
 */
export function navActionsForForm(
  id: string,
  actionMap: Record<string, NavActions> | undefined | null,
  isAdmin: boolean,
): NavActions {
  if (isAdmin) return ALL_NAV_ACTIONS;
  if (!actionMap || Object.keys(actionMap).length === 0) return ALL_NAV_ACTIONS;

  const views = NAV_VIEW_MAPPING[id] ?? [];
  let matched = false;
  const acc: NavActions = { read: false, write: false, create: false, delete: false };
  for (const v of views) {
    const p = actionMap[v.toLowerCase()];
    if (p) {
      matched = true;
      acc.read ||= p.read;
      acc.write ||= p.write;
      acc.create ||= p.create;
      acc.delete ||= p.delete;
    }
  }
  return matched ? acc : ALL_NAV_ACTIONS;
}
